import { z } from 'zod';
import { PayoutFrequencySchema } from '../billing/enums.schema.js';
/** Canadian SIN: 9 digits, dash- or space-separated formatting accepted.
 *  Normalised to digits-only before storage. */
export const SinSchema = z
    .string()
    .regex(/^\d{9}$|^\d{3}[- ]\d{3}[- ]\d{3}$/, 'SIN must be 9 digits (with optional dashes/spaces)')
    .transform((s) => s.replace(/[- ]/g, ''));
/** CRA HST/GST registration number: 9 digits + 'RT' + 4 digits.
 *  Example: 123456789RT0001 */
export const HstNumberSchema = z
    .string()
    .regex(/^\d{9}RT\d{4}$/i, 'HST number must be in the format 123456789RT0001')
    .transform((s) => s.toUpperCase());
/** EFT banking details for caregiver payouts (Q9 — biweekly RBC Express).
 *  Validation reflects Canadian Payments Association format:
 *  - Transit (branch): 5 digits
 *  - Institution: 3 digits
 *  - Account: 7–12 digits
 *  Storage is encrypted at the application layer via ColumnCipherService. */
export const EftBankingSchema = z.object({
    transit: z.string().regex(/^\d{5}$/, 'Transit must be 5 digits'),
    institution: z.string().regex(/^\d{3}$/, 'Institution must be 3 digits'),
    account: z.string().regex(/^\d{7,12}$/, 'Account must be 7 to 12 digits'),
});
/** POST /api/v1/caregivers/me/tax-info body.
 *
 *  Collected AFTER activation as a single "complete tax + banking info"
 *  step (Phase 5 scope decision — PII minimisation, see PIPEDA Principle 4
 *  + storage-liability rationale). All fields are encrypted at rest via
 *  ColumnCipherService; the request body never echoes back through any
 *  GET endpoint — only boolean on-file flags are exposed.
 *
 *  Banking void cheque image is uploaded separately via the standard
 *  signed-URL flow (eftVoidChequeKey on the caregiver row). */
export const CaregiverTaxInfoRequestSchema = z.object({
    sin: SinSchema,
    banking: EftBankingSchema,
    voidChequeObjectKey: z.string().min(1).max(500),
    hstRegistered: z.boolean(),
    hstNumber: HstNumberSchema.nullable().optional(),
}).refine((data) => !data.hstRegistered || !!data.hstNumber, { message: 'hstNumber is required when hstRegistered is true', path: ['hstNumber'] });
/** Response after successful tax-info submission. No PII echoed back. */
export const CaregiverTaxInfoResponseSchema = z.object({
    sinOnFile: z.literal(true),
    eftOnFile: z.literal(true),
    hstRegistered: z.boolean(),
    // The caregiver is now eligible for matching (Phase 9) gates on this.
    eligibleForMatching: z.boolean(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Phase 11b-1 — resource-per-concern endpoints.
//
// /me/banking — banking-only updates (post-activation, common case)
// /me/hst     — HST registration toggle (when caregiver crosses CRA $30K threshold)
// /me/tax-info — kept as thin orchestration wrapper (bulk activation submit)
// ─────────────────────────────────────────────────────────────────────────────
/** PATCH /me/banking request body. Banking-only — does NOT touch SIN or HST. */
export const CaregiverBankingPatchRequestSchema = z.object({
    banking: EftBankingSchema,
    voidChequeObjectKey: z.string().min(1).max(500),
});
/** PATCH /me/banking response — banking state after submission. */
export const CaregiverBankingPatchResponseSchema = z.object({
    eftSubmitted: z.literal(true),
    eftVerified: z.literal(false),
});
/** GET /me/banking response — masked banking state + verification status +
 *  current payout frequency. Per 11b-2 the caregiver-side response masks
 *  the account number (last-4 only) and transit (last-3); the operator
 *  admin endpoint returns the full decrypted values for keying into the
 *  bank's web portal. */
export const CaregiverBankingResponseSchema = z.object({
    eftSubmitted: z.boolean(),
    eftVerified: z.boolean(),
    eftRejected: z.boolean(),
    eftRejectionReason: z.string().nullable(),
    eftSubmittedAt: z.string().datetime().nullable(),
    eftVerifiedAt: z.string().datetime().nullable(),
    /** Last 4 digits of the account number, e.g. "1234". null when no
     *  banking has been submitted. */
    accountLast4: z.string().nullable(),
    /** Last 3 digits of the 5-digit transit number. */
    transitLast3: z.string().nullable(),
    /** Full 3-digit institution number (not sensitive — names the bank). */
    institution: z.string().nullable(),
    hasVoidChequeOnFile: z.boolean(),
    /** Current cadence (read-only on this endpoint per 11-2; operator-
     *  controlled via the admin payout-frequency endpoint). */
    payoutFrequency: PayoutFrequencySchema,
});
/** PATCH /me/hst request body. HST registration toggle. */
export const CaregiverHstPatchRequestSchema = z.object({
    hstRegistered: z.boolean(),
    hstNumber: HstNumberSchema.nullable().optional(),
}).refine((data) => !data.hstRegistered || !!data.hstNumber, { message: 'hstNumber is required when hstRegistered is true', path: ['hstNumber'] });
/** PATCH /me/hst response. */
export const CaregiverHstPatchResponseSchema = z.object({
    hstRegistered: z.boolean(),
    hstNumber: z.string().nullable(),
});
/** PATCH /me/sin request body (Phase 5g — standalone SIN submission so the
 *  ONBOARDING-stage caregiver can provide SIN without bundling banking).
 *  Thin alias for the INDIVIDUAL branch of /me/tax-identity (kept for
 *  backward compat). */
export const CaregiverSinPatchRequestSchema = z.object({ sin: SinSchema });
/** PATCH /me/sin response. No PII echoed — on-file flag + whether this
 *  submission auto-activated the caregiver (last onboarding gate item). */
export const CaregiverSinPatchResponseSchema = z.object({
    sinOnFile: z.literal(true),
    activated: z.boolean(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Tax identity — INDIVIDUAL (SIN) or CORPORATION (Business Number). Exactly one
// identifier per caregiver; the caregiver picks their entity type + provides the
// matching identifier. The onboarding gate reads whichever matches.
// ─────────────────────────────────────────────────────────────────────────────
export const CaregiverTaxEntityTypeSchema = z.enum(['INDIVIDUAL', 'CORPORATION']);
/** 9-digit CRA Business Number. Accepts a pasted program account (e.g.
 *  "123456789RC0001") and normalises to the 9-digit BN. Not encrypted at rest
 *  (identifies the corporation, not a person — not PIPEDA personal info). */
export const BusinessNumberSchema = z
    .string()
    .trim()
    .regex(/^\d{9}(\s?[A-Za-z]{2}\s?\d{4})?$/, 'Business Number must be 9 digits (optionally with a program account like RC0001)')
    .transform((s) => s.replace(/\s/g, '').slice(0, 9));
/** PATCH /me/tax-identity — set the entity type + its identifier atomically.
 *  One-of by taxEntityType, enforced via refine (a plain object, not a
 *  discriminated union, so nestjs-zod's createZodDto can wrap it). */
export const CaregiverTaxIdentityPatchRequestSchema = z
    .object({
    taxEntityType: CaregiverTaxEntityTypeSchema,
    sin: SinSchema.optional(),
    businessNumber: BusinessNumberSchema.optional(),
})
    .refine((d) => (d.taxEntityType === 'CORPORATION' ? !!d.businessNumber : !!d.sin), {
    message: 'Provide a SIN for an INDIVIDUAL, or a Business Number for a CORPORATION',
    path: ['taxEntityType'],
});
/** PATCH /me/tax-identity response. No SIN/BN echoed — entity type + on-file
 *  flag + whether this submission auto-activated the caregiver. */
export const CaregiverTaxIdentityPatchResponseSchema = z.object({
    taxEntityType: CaregiverTaxEntityTypeSchema,
    identifierOnFile: z.literal(true),
    activated: z.boolean(),
});
/** GET /me/tax-identity — current tax-identity state. No SIN/BN echoed; the
 *  BN is not sensitive so its last-3 are shown for confirmation, mirroring the
 *  banking mask pattern. */
export const CaregiverTaxIdentityResponseSchema = z.object({
    taxEntityType: CaregiverTaxEntityTypeSchema,
    sinOnFile: z.boolean(),
    businessNumberOnFile: z.boolean(),
    /** Last 3 digits of the BN for confirmation (BN isn't sensitive). null when
     *  no BN on file. */
    businessNumberLast3: z.string().nullable(),
});
//# sourceMappingURL=tax-info.schema.js.map